/**
 * useSpanReplace.ts — PLAT-10214 (T6, epic PLAT-10209)
 *
 * Orchestrates the workspace span-replace flow (epic D3 + D10):
 *
 *   1. Watch the TRANSLATED column for a contiguous-run selection
 *      (`resolveTranslatedSelectionPhrases`, scoped to the column ref —
 *      CLAUDE.md #12). A valid run surfaces a "Fix Translation" trigger anchored
 *      at the selection.
 *   2. Open the `ReplaceSpanEditor` pre-filled with the run's current
 *      translation; light + lock the mapped ORIGINAL region in the left pane
 *      (`applySpanReplaceLock`).
 *   3. Confirm → author a reversible override segment
 *      (`TranslationStore.createOverrideSegment`, `fragment: true` so the T9
 *      splitter tiles it) — OR, when the span already sits inside one
 *      manual/directed segment, re-edit it (`updateOverrideSegment`, D10
 *      re-edit) — OR, when the span partially overlaps one or more existing
 *      corrections, ABSORB them into the new one (`absorbIntoOverrideSegment`,
 *      PLAT-11655). Fire `trackTranslationReview_SpanReplaced`.
 *   4. Cancel → clear the lock + selection, restore.
 *
 * Containment (D9): everything lives in the workspace tree. The hook registers
 * a `selectionchange` listener only while `enabled`, scoped to the passed column
 * refs; nothing leaks into the shared renderers when off.
 *
 * The store stays pure: the splitter is INJECTED as `fragmenter`. When the T9
 * endpoint isn't wired (or unavailable), pass `undefined` and the override
 * lands as a single fragment (the store's fallback) — still fully reversible.
 */

import {
  trackTranslationReverted,
  trackTranslationReview_DirectedRetranslate,
  trackTranslationReview_DirectedRetranslateCompleted,
  trackTranslationReview_DirectedRetranslateFailed,
  trackTranslationReview_SpanReplaced,
} from '@src/analytics/EventTracker';
import { isEditableElementFocused } from '@src/services/shortcuts/keyboard';
import {
  resolveTranslatedSelectionPhrases,
  type SelectionResolution,
} from '@src/stores-v2/resolveTranslatedSelectionPhrases';
import { useStore, useTranslationStore } from '@src/stores-v2/StoreContext';
import {
  buildSpanDisplayModel,
  joinFragments,
  overrideSegmentIdsTouchedBy,
  type SpanDisplayModel,
  spanTouchesOverride,
  unionOriginalIndices,
} from '@src/stores-v2/translationSpanEdit';
import { type SegmentFragmenter } from '@src/stores-v2/TranslationStore';
import { LogManager } from '@src/utility/Logger';
import {
  applySpanReplaceLock,
  clearSpanReplaceLock,
} from '@src/utility/spanReplaceLockHighlight';
import { joinPhraseValues } from '@src/utility/strings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  resolveVanishedSpanSource,
  type SourceBaselineEntry,
} from '../editableOriginal';
import { singleStaleLeadOfSelection } from '../staleSelection';
import { isEdited } from '../translatedPhraseEditing';

import type { TranslationViewMode } from '../alignedBlocks';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type {
  ParentPhrase,
  TranslationPhrase,
} from '@src/types.ts/translation';
import type { RefObject } from 'react';

const logger = LogManager.getLogger('feature:span-replace');

/**
 * Why a span is refused: the reason the editor renders as its warning panel.
 *
 * Kept as a union rather than collapsed to a boolean, deliberately: the two
 * refusals read differently to the reviewer, and `SPAN_BLOCK_MESSAGES` below is
 * a TOTAL map, so a member added without copy is a compile error. A span that
 * merely overlaps an existing correction is not a refusal at all — Confirm
 * absorbs the correction (PLAT-11655).
 */
export type SpanBlockReason =
  | 'spans-multiple-speakers'
  | 'absorb-wrote-nothing';

/**
 * Copy shown when a span that TOUCHES an existing correction (re-edit, partial
 * overlap, or spanning two) is asked for a MACHINE re-translate. The directed
 * path refuses any such span (locked decision 2026-07-16) because a machine
 * result would overwrite part of a typed correction; the manual path is open
 * for every one of them (PLAT-11655 absorbs the overlap on Confirm), so the
 * copy points the reviewer at it. Sentence case (design system: prose, not a
 * button label).
 *
 * Named for what it says: this is no longer a span BLOCK REASON (nothing sets
 * one for an overlap since PLAT-11655) — it is the "machine re-translate is not
 * available for this selection" notice, and its only consumers are that path.
 */
export const SPAN_MACHINE_RETRANSLATE_UNAVAILABLE_MESSAGE =
  'This selection overlaps an existing correction, so it cannot be re-translated by machine. Type the replacement instead.';

/**
 * Copy shown when a selection crosses a change of SPEAKER (PLAT-10945). One
 * correction takes its identity from its earliest covered word, so a correction
 * spanning two speakers is credited entirely to the first: the translated prose
 * view pins it inside that speaker's section, copy/export writes it at that first
 * word, and — because the speaker chooses the dubbing voice — the dubbed audio
 * speaks one person's words in the other person's voice. So the crossing is
 * refused while the selection is still on screen rather than discovered in the
 * output. Unlike paragraphs, speakers ARE drawn on these screens, so the copy
 * names the reason the reviewer can actually see. Sentence case (design system:
 * prose, not a button label).
 */
export const SPAN_CROSS_SPEAKER_BLOCK_MESSAGE =
  'This selection covers more than one speaker. Fix one speaker at a time.';

/**
 * Copy shown when the absorb write returned without authoring anything — the
 * store refused the span (a correction it would have to absorb retired no
 * machine original, so absorbing it would leave words with no text and nothing
 * to undo back to), or the cached translation moved on under the editor.
 * Nothing was changed either way, which is what the reviewer needs told: the
 * editor closed optimistically when Confirm was pressed, so without this the
 * typed text would appear to have been accepted and then vanished. Sentence
 * case (design system: prose, not a button label).
 */
export const SPAN_ABSORB_REFUSED_MESSAGE =
  'This selection could not be replaced, so nothing was changed. Fix the words inside one existing correction instead.';

/**
 * The notice copy for each block reason (drives the editor's warning panel). A
 * total map, so the compiler requires copy for every member of the union.
 */
const SPAN_BLOCK_MESSAGES: Record<SpanBlockReason, string> = {
  'spans-multiple-speakers': SPAN_CROSS_SPEAKER_BLOCK_MESSAGE,
  'absorb-wrote-nothing': SPAN_ABSORB_REFUSED_MESSAGE,
};

/** The notice copy for a block reason (drives the editor's blocked state). */
export function spanBlockMessage(reason: SpanBlockReason): string {
  return SPAN_BLOCK_MESSAGES[reason];
}

/**
 * Why `confirmDirected` refused or failed (PLAT-10257 A2). The `blocked-*`
 * members are pre-flight refusals — the span may not be directed-re-translated
 * at all (locked decision 2026-07-16: ANY touch of an existing override —
 * within / exact-match / extending / spans-two — is refused on the directed
 * path; the manual path keeps its within-one-override re-edit). The rest are
 * failures after the directed path started: a covered source index with no
 * source element, a backend (re-translate) failure, or the override authoring
 * itself failing. A3 (mode toggle) and A6 (error notice) consume these.
 */
export type ConfirmDirectedFailureReason =
  | 'no-pending-span'
  | 'blocked-within-fix'
  | 'blocked-overlap'
  | 'blocked-cross-speaker'
  | 'missing-source-element'
  | 'backend-failed'
  | 'author-failed';

/**
 * The discriminated outcome of `confirmDirected` (PLAT-10257 A2). `ok` carries
 * the created override segment id (for undo / follow-up); a non-ok result
 * names the reason and guarantees NOTHING was authored or persisted.
 */
export type ConfirmDirectedResult =
  | { ok: true; segmentId: string }
  | { ok: false; reason: ConfirmDirectedFailureReason };

/**
 * PLAT-12022 — what "Re-dub AI Voice" hands the host: enough to re-record the
 * selection and to open the popover beside it, without the host re-deriving
 * any of it from the dub.
 */
export interface ReDubSelectionRequest {
  /** The selection's distinct period-level `p_` ids — the run's `segmentIds`. */
  segmentIds: string[];
  /**
   * The transcript labels (`spk_X` / `override_<id>`) of the speakers whose
   * words the selection covers, in source order. Usually one; two when the
   * selection crosses a speaker change, which a re-dub still allows.
   */
  speakerLabels: string[];
  /** The selection's first word — the popover anchors to it. */
  anchorElement: HTMLElement;
  /** The translated words the selection covers — what will be re-recorded. */
  translatedText: string;
  /** The source-language words under them, for reference. */
  sourceText: string;
}

/** A resolved, replaceable selection awaiting the "Fix Translation" trigger. */
export interface PendingSpan {
  /** Anchor element for the trigger button + the editor (the first word). */
  anchorElement: HTMLElement;
  /** Pre-fill model for the editor (current value, snapshot, covered indices). */
  model: SpanDisplayModel;
  /**
   * The machine-original translation of the span — what it said before any
   * override / individual edit — for the editor's read-only "Original
   * translation" row (PLAT-10265). Re-edit: the retired fragments this override
   * superseded (their machine text is untouched). Fresh span: the covered
   * phrases' captured machine baseline. Undefined when it can't be resolved.
   */
  originalTranslatedValue?: string;
  /**
   * Set when the run can't be replaced as-is: it crosses a change of SPEAKER
   * (fix one speaker at a time — PLAT-10945). A clean re-edit of one override
   * is NOT blocked (it updates in place), a PARAGRAPH crossing is not blocked
   * (PLAT-10945), and an OVERLAP with existing corrections is not blocked
   * either since PLAT-11655 (Confirm absorbs them — see
   * `overlappingOverrideSegmentIds`). The editor shows a notice + hides
   * Confirm; `confirm` no-ops.
   */
  blockedReason?: SpanBlockReason;
  /**
   * PLAT-12022 — the transcript labels of every speaker the covered source
   * words belong to, in source order. The same set `blockedReason` is judged
   * on; kept so a re-dub can name who is being re-recorded.
   */
  speakerLabels: string[];
  /**
   * PLAT-11655 — the existing corrections this span OVERLAPS without sitting
   * entirely inside one: a partial overlap with one, or a span across two or
   * more (the singular `existingOverrideSegmentId` is undefined for both).
   * Confirm ABSORBS every one of them into the new correction
   * (`absorbIntoOverrideSegment`); the editor tells the reviewer how many
   * (`length`). Undefined on a clean re-edit, a fresh span, and a cross-speaker
   * block. The input is prefilled with the SELECTED fragments' current text
   * only — the widening to the union span happens in the data, not on screen.
   */
  overlappingOverrideSegmentIds?: readonly string[];
  /**
   * The reviewer who last applied this change (PLAT-10444) — the override
   * segment's `editedBy` on a re-edit, or the most-recently-edited covered
   * phrase's on a single-phrase fix. Undefined for a fresh span / edits made
   * before attribution shipped. Drives the editor's "Last edited" line.
   */
  editedBy?: { name: string; email?: string; at?: string };
  /**
   * The source indices the reviewer's selection ACTUALLY covered, before
   * Segment view widened it (PLAT-11340 follow-up).
   *
   * `model.coveredIndices` is the span the write lands on, and in Segment view
   * that is the whole segment — `resolvePending` widens via
   * `expandToWholeSegments` so a correction can't corrupt a partially-covered
   * segment. For "Fix Translation" the widening is invisible: the editor
   * pre-fills with the whole segment and the reviewer edits what they see.
   * "Remove Translation" writes blind, so it needs to know which words were
   * pointed at — without this, selecting "eh," in Segment view blanked the
   * entire segment.
   *
   * Equal to `model.coveredIndices` whenever no widening happened (Fragment /
   * Paragraph view, or a selection that already filled its segment).
   */
  selectedIndices: readonly number[];
  /**
   * The text the EDITOR shows and the reviewer types over (PLAT-11648).
   *
   * `model.currentTranslatedValue` is the whole covered run, which in Segment
   * view is the whole segment — so highlighting one word opened a box holding
   * the whole sentence and the reviewer had to find their word inside it. This
   * is the SELECTED fragments' text instead, so the box holds what was pointed
   * at. `confirm` puts the edited text back where it came from via
   * `spliceSelectedValue`, so the WRITE still covers the widened span and the
   * anti-corruption reason for widening is untouched.
   *
   * Equal to `model.currentTranslatedValue` whenever nothing was widened, and
   * whenever the span re-edits a stored override (whose byte-exact value has no
   * fragment structure to splice into — see `buildSpanDisplayModel`).
   */
  editableValue: string;
  /**
   * How many words Confirm will replace — the number the editor shows under the
   * input. It is the highlight only where the box holds the highlight; where the
   * box holds the whole covered run (a span re-editing a stored override, or an
   * un-widened view) it is that whole run. Derived from the SAME predicate as
   * `editableValue` so the sentence and the box can never disagree (pull request
   * #3566 review: the line read "Replacing 1 word" while Confirm replaced two).
   */
  replacingWordCount: number;
}

export interface UseSpanReplaceParams {
  /** Opt-in (D9). When false, no listener is registered and nothing renders. */
  enabled: boolean;
  /** The translated language under review (BCP 47). */
  language: string;
  /**
   * The active aligned view mode. In `'segment'` the column renders ONE word per
   * SEGMENT (stamped with the segment's lead index only), so a selection must be
   * expanded to the whole segment before authoring an override — otherwise it
   * would overwrite just the lead fragment and leave the rest stale.
   */
  viewMode: TranslationViewMode;
  /** The translated (middle) column element — selection is read here, scoped. */
  translatedColumnRef: RefObject<HTMLElement>;
  /** The original (left) column element — the lock highlight paints here. */
  originalColumnRef: RefObject<HTMLElement>;
  /**
   * The T9 splitter (PLAT-10269), injected. When supplied, a confirmed override
   * is fine-split into word-level fragments; when omitted the override lands as
   * a single fragment (still reversible). Keeps the store network-free.
   */
  fragmenter?: SegmentFragmenter;
  /**
   * PLAT-10268 (T8) — fired after a confirmed span replacement is PERSISTED, on
   * both persist paths (re-edit and fresh override). The workspace passes a
   * handler that runs the coverage check for the span and caches the verdict.
   * Optional + fire-and-forget: `confirm` never awaits it, so the coverage call
   * can never block or delay the save (invariant F). Absence is a no-op.
   */
  onSpanConfirmed?: (info: {
    lang: string;
    coveredElementIds: string[];
  }) => void;
  /**
   * PLAT-10616 (T13) — span re-dub. When supplied, the selection affordance
   * offers a second action, "Re-dub AI Voice", alongside Fix Translation. It
   * hands the selection up to the host as a `ReDubSelectionRequest` — its
   * covered period-level segment ids (the distinct `parentId`s), the speakers
   * who say them, and the word to anchor to — and the host opens the
   * selection re-dub popover scoped to just that section (`regenerate: true`
   * over those segments — additive, DE2/DE5; PLAT-12022). Omit (or when the
   * active language has no dubbing) and only Fix Translation shows.
   */
  onRequestReDub?: (request: ReDubSelectionRequest) => void;
  /**
   * PLAT-10266 (Card 7, §D) — the load-time source baseline
   * (`captureSourceBaseline`). Used ONLY as a fallback when authoring an override
   * whose covered source span VANISHED (a "Replace Term" deleted the covered
   * token): the confirm / directed-re-translate paths then resolve the
   * replacement's added tokens at the baseline slot for the re-translate source
   * AND re-key the authored override to those tokens' fresh ids so it renders by
   * identity instead of re-orphaning. Undefined ⇒ the pre-Card-7 behavior (a
   * vanished span stays `missing-source-element`).
   */
  sourceBaseline?: ReadonlyMap<string, SourceBaselineEntry>;
  /**
   * PLAT-10266 (Card R2) — the STALE (amber) fragment leads, canonical keys
   * (`min(originalIndices)`). Supplied only by the review workspace, which is the sole
   * host offering the resolve actions. Without it this hook cannot tell a stale fragment
   * from any other selection — the wiring gap that made highlighting an amber fragment
   * open "Fix Translation" instead of the resolve menu, reproduced live in all three
   * views (logs/repro-stale-highlight-20260729/).
   *
   * OPTIONAL by design: the hook's two other consumers (the inline prose translated view
   * and the translated captions view, both via `InlineSpanReplaceLayer`) pass neither
   * this nor the callback below, and their behavior is unchanged.
   */
  staleLeads?: ReadonlySet<number>;
  /**
   * PLAT-10266 (Card R2) — fired on every selection resolve with the single stale
   * fragment the selection covers, or `null` for every other selection (mixed, several
   * stale fragments, or not stale at all).
   *
   * It reports `null` too, rather than only firing on a hit, because the host also uses
   * this to keep the right-click path in step: right-clicking a stale word while a
   * selection covers it must route the same way the highlight did, and the selection
   * precedence bail runs BEFORE the stale check. A callback that only fired on hits would
   * leave a stale value behind and mis-route a later right-click.
   */
  onStaleSelectionChange?: (
    hit: { lead: number; anchorElement: HTMLElement } | null,
  ) => void;
}

export interface SpanReplaceController {
  /** A resolved selection awaiting the trigger, or null. */
  pendingSpan: PendingSpan | null;
  /** The open editor's state (anchor + model), or null. */
  editor: PendingSpan | null;
  /** Open the editor for the current pending span (from the trigger click). */
  openEditor: () => void;
  /**
   * Re-dub the current pending span (PLAT-10616): resolve its covered
   * period-level segment ids and hand them to the host's `onRequestReDub`, then
   * dismiss the affordance. No-op when re-dub isn't wired or the span covers no
   * segment. See `canReDub`.
   */
  reDub: () => void;
  /**
   * Whether the "Re-dub AI Voice" action should show for the current pending
   * span — re-dub is wired (a dubbing-enabled translated language) AND the
   * selection covers at least one period-level segment.
   */
  canReDub: boolean;
  /**
   * PLAT-10833 — the context menu's reads, taken at the moment of the
   * click: the pending span when the trigger's resolve produced one, else
   * the live selection resolved now. `reDubSelection` returns whether a
   * re-dub was handed off.
   */
  canReDubSelection: () => boolean;
  reDubSelection: () => boolean;
  /**
   * PLAT-12056 — the span a menu action is about: the pending one, else the
   * live selection resolved now. For the Fix Translation hand-off, which
   * needs its covered indices and lines without opening the editor here.
   */
  peekSelectionSpan: () => PendingSpan | null;
  /**
   * Remove the current span's translation (PLAT-11340 follow-up) — authors the
   * covered run as an empty correction, leaving the source words untouched.
   * Runs from the trigger (no editor open) as well as from the editor.
   */
  removeSpan: () => Promise<void>;
  /**
   * PLAT-10257 (A4) — open the editor directly for an EXPLICIT set of covered
   * source `initial_index` values, with no DOM selection. The coverage-popover
   * entry point: the popover is anchored per-SEGMENT and never flows through
   * the selection resolver, so it calls this with its entry's covered indices
   * (the same span its Undo reverts). Produces the exact state `openEditor`
   * leaves behind (pendingSpan + editor hold the same span) — the guards
   * (overlap via `spanTouchesOverride`, cross-speaker) and the re-edit
   * signal are computed HERE, at open time, from current store data, so the
   * popover path can never reach the op unguarded (the silent-no-op defect).
   */
  openForIndices: (coveredIndices: readonly number[]) => void;
  /**
   * Confirm the typed correction → author / re-edit the override segment, or
   * — when the span overlaps existing corrections (PLAT-11655) — absorb them
   * into the new one.
   */
  confirm: (value: string) => Promise<void>;
  /**
   * PLAT-10257 (A2) — directed span re-translate. Re-translates the OPEN
   * editor's covered SOURCE span via the backend, steered by the reviewer's
   * free-text `instruction`, and lands the result as a reversible
   * `origin: 'directed'` override segment (one undo restores the exact
   * original); the instruction is recorded on the change entry. Refuses ANY
   * span touching an existing override (within / exact-match / extending /
   * spans-two — locked decision 2026-07-16) and a cross-SPEAKER span;
   * authors nothing on a backend failure. Every non-ok outcome leaves the
   * pending span/editor state INTACT so the host can render the reason (A6);
   * only a successful authoring closes the editor.
   */
  confirmDirected: (instruction: string) => Promise<ConfirmDirectedResult>;
  /**
   * Whether the open editor's covered span has nothing applied to revert — the
   * "Revert to original" button is shown but disabled when true. True for a
   * fresh span (no edit yet) and a draft that hasn't been confirmed; false once
   * any covered phrase is edited (a phrase-record edit OR an override fragment),
   * via the shared `isEdited` predicate.
   */
  revertDisabled: boolean;
  /**
   * Revert the OPEN editor's covered span to its machine original (PLAT-10265).
   * Routes by data shape: an override span drops the whole override segment
   * (`revertOverrideSegment`); a span overlapping corrections drops each of
   * them (PLAT-11655); a phrase-record edit (e.g. a single-word highlight that
   * delegated to `editTranslatedPhrase`) reverts each covered phrase
   * (`revertTranslation`). Restores the machine fragments, then closes.
   */
  revertOverride: () => Promise<void>;
  /** Cancel — clear the lock + dismiss editor and trigger. */
  cancel: () => void;
}

/**
 * Read the SOURCE words rendered in the original (left) pane for `indices`,
 * joined in source order — the editor's "Original text" fallback when the
 * covered phrases carry no `originalValueSnapshot` (true for plain
 * machine-translation phrases; only override fragments populate it). Scoped to
 * `container` so a second transcript instance on the page is never read
 * (CLAUDE.md #12). Missing/whitespace spans are skipped.
 */
function readOriginalPaneText(
  container: HTMLElement | null,
  indices: readonly number[],
  // PLAT-10611 (F5, R13) — the covered content words' stable element ids, in
  // source order. When present, read the original pane by `data-source-uuid`
  // FIRST — the true cross-pane drift fix: on a reordered clip the source word
  // for a covered phrase is at the CORRECT id even though its `initial_index`
  // has shifted. Falls back to the positional `data-initial-index` when no ids
  // (legacy blob) or none matched a span (§6).
  elementIds?: readonly string[],
): string {
  if (!container) return '';
  if (elementIds && elementIds.length > 0) {
    const parts: string[] = [];
    for (const id of elementIds) {
      const span = container.querySelector<HTMLElement>(
        `[data-source-uuid='${id}']`,
      );
      const text = span?.textContent?.trim();
      if (text) parts.push(text);
    }
    if (parts.length > 0) return joinPhraseValues(parts);
    // No id matched a span (spans un-stamped / legacy) → integer fallback below.
  }
  const parts: string[] = [];
  for (const idx of indices) {
    const span = container.querySelector<HTMLElement>(
      `[data-initial-index='${idx}']`,
    );
    const text = span?.textContent?.trim();
    if (text) parts.push(text);
  }
  return joinPhraseValues(parts);
}

/**
 * Source-text fallback for hosts with NO original pane (the inline surfaces pass
 * a null `originalColumnRef`, so `readOriginalPaneText` returns ''). Reads the
 * SOURCE word values straight from the transcript store's elements for
 * `indices`, joined in source order — so the editor's "Original text" is
 * populated inline too, not only in the 3-pane workspace. Mirrors
 * `readOriginalPaneText`'s output (trimmed words, whitespace/empty skipped,
 * single-space joined).
 */
function readSourceTextFromElements(
  byIndex: Map<number, TranscriptElement>,
  indices: readonly number[],
): string {
  const parts: string[] = [];
  for (const idx of indices) {
    const text = byIndex.get(idx)?.value?.trim();
    if (text) parts.push(text);
  }
  return joinPhraseValues(parts);
}

/**
 * The CONTIGUOUS run of SOURCE elements spanning a covered selection: the slice
 * of the ordered `transcriptionElements` from the FIRST covered element through
 * the LAST, inclusive. This KEEPS the interleaved whitespace/punctuation
 * elements between the words — whitespace carries `initial_index = -1` and no
 * UUID, so it is absent from both `coveredElementIds` and `coveredIndices`, yet
 * the backend fragmenter (`splitIntoSentenceFragments`) appends EVERY element's
 * value to build the translator payload, so without the whitespace the
 * re-translated span comes back run-together (PLAT-10257 A7: `Ithink,if…`
 * instead of `I think, if …`). Leading/trailing whitespace OUTSIDE the span is
 * naturally excluded — the slice starts and ends on covered content elements. A
 * span is always contiguous (the selection resolver enforces it), so the slice
 * is exactly the reviewer's selected source, whitespace and all.
 *
 * The span's first/last SOURCE POSITION is located by the STABLE UUID
 * (`source_uuid ?? id`, matched against `coveredElementIds`), NOT the integer
 * `initial_index` — the integer is CLIP-LOCAL (regenerated per clip snapshot) so
 * a story-space `coveredIndices` set misses in clip scope and refuses a valid
 * span (PLAT-10257 clip bug). The integer path survives only as the LEGACY
 * fallback for id-less pre-UUID data (`coveredElementIds` empty), matching the
 * codebase's UUID-first / integer-fallback join pattern.
 *
 * Returns `null` ONLY when NONE of the covered UUIDs can be located in the
 * source array — a span we cannot place at all, which the caller surfaces as
 * `missing-source-element` rather than silently skipping (the backend must never
 * re-translate a DIFFERENT source than selected). When at least one covered UUID
 * matches, the span is bounded by the matched positions and the inclusive slice
 * tolerates a covered element the clip re-tokenized without preserving its
 * `source_uuid` — see the matched-bound note in the body.
 *
 * PLAT-10266 Card I4 — a covered word REPLACED by more words. Since Cards I1/I2 the
 * i-th replacement word inherits the i-th replaced word's stable key
 * (`inheritedSourceUuidsFor`), so the surplus words inherit nothing and the matched
 * bound stopped on the FIRST replacement word: re-translating "secret" edited to
 * "hidden truth" sent only "hidden" to the backend. Before those cards the same edit
 * made this return `null`, and the caller's `resolveVanishedSpanSource` fallback
 * swept the whole run up — so nothing was lost, and the sweep has to happen here
 * now. See the trailing-run extension in the body.
 */
export function collectSpanSourceElements(
  transcriptionElements: readonly TranscriptElement[],
  coveredElementIds: readonly string[],
  coveredIndices: readonly number[],
): TranscriptElement[] | null {
  let first = Infinity;
  let last = -Infinity;

  // PRIMARY — key on the stable UUID (clip-scope-safe). Every content element
  // carries `source_uuid`/`id`; whitespace carries neither (skipped here, kept
  // by the inclusive slice below).
  if (coveredElementIds.length > 0) {
    const positionByUuid = new Map<string, number>();
    transcriptionElements.forEach((el, pos) => {
      const uuid = el.source_uuid ?? el.id;
      if (uuid !== undefined && !positionByUuid.has(uuid)) {
        positionByUuid.set(uuid, pos);
      }
    });
    // Bound the span by the covered UUIDs that DO match, then take the inclusive
    // positional slice. A covered element the clip re-tokenized WITHOUT
    // preserving its `source_uuid` — e.g. a contraction like "they're" that the
    // clip re-keyed — is not matched by UUID, but it sits positionally BETWEEN
    // its matched neighbours, so the slice still captures it (exactly as the
    // slice already captures interleaved whitespace). Requiring EVERY covered
    // UUID to match would refuse the whole span over one such diverged interior
    // element (the clip-boundary bug). Fail (null) only when NOTHING matched: a
    // span we cannot locate at all.
    let matchedCount = 0;
    for (const uuid of coveredElementIds) {
      const pos = positionByUuid.get(uuid);
      if (pos === undefined) continue;
      matchedCount += 1;
      if (pos < first) first = pos;
      if (pos > last) last = pos;
    }
    if (matchedCount === 0) return null;
    // Card I4 — the span ends on an INSERTED token (`initial_index < 0`), which
    // means a covered key resolved onto a replacement word. Extend across the rest
    // of that replacement run so the backend receives the whole edited text.
    // Bounded three ways: at the first surviving original word; at a FOREIGN ANCHOR
    // (an inserted content word carrying a key this span does not cover — since
    // Cards I1/I2 that is exactly where the NEXT fragment's replacement run
    // begins); and on the last inserted WORD rather than on a trailing spacer.
    // The leading end deliberately gets no such extension: the FIRST replacement
    // word takes the FIRST replaced word's key, so the span already opens on the
    // run and reaching backward could only take the previous fragment's words.
    const isInsertAt = (pos: number): boolean =>
      (transcriptionElements[pos]?.initial_index ?? 0) < 0;
    const isBlankAt = (pos: number): boolean => {
      const value = transcriptionElements[pos]?.value;
      return typeof value === 'string' && value.trim() === '';
    };
    // Card I6 — a comma or full stop at the boundary between this span's
    // replacement run and the NEXT fragment's belongs to THIS span, the one on
    // its left. Only text tokens inherit a key, so such a mark is not a foreign
    // anchor and nothing in the data says which run produced it: an array built
    // by editing one row's word to `x y,` and the next row's to `p q` is
    // byte-identical to one built by editing them to `x y` and `, p q`, down to
    // the minted ids (measured, Card I6). Card I5 resolved that by ending on the
    // last inserted WORD, which meant the mark reached NEITHER payload — the next
    // fragment's own first key resolves onto its own first word, so its span
    // starts after the mark. Ending on the last inserted NON-BLANK token instead
    // puts the mark in exactly one payload: this one. It is the payload of the
    // row that typed the mark whenever the replacement ENDS with it, which is the
    // ordinary way a reviewer types one; when the FOLLOWING fragment's
    // replacement begins with a mark, this span sends one mark too many. That is
    // the accepted cost of exactly-once, and it keeps
    // `renderOriginalWords`'s slice and this payload identical — the two must
    // agree or the row shows text the backend did not receive. The trailing
    // spacer is still excluded, so no payload ends in whitespace.
    const covered = new Set(coveredElementIds);
    if (isInsertAt(last)) {
      let runEnd = last;
      let scan = last + 1;
      while (scan < transcriptionElements.length && isInsertAt(scan)) {
        const key = transcriptionElements[scan]?.source_uuid;
        const isForeignAnchor =
          key !== undefined && !isBlankAt(scan) && !covered.has(key);
        if (isForeignAnchor) break;
        if (!isBlankAt(scan)) runEnd = scan;
        scan += 1;
      }
      last = runEnd;
    }
    return transcriptionElements.slice(first, last + 1);
  }

  // LEGACY FALLBACK — id-less pre-UUID blob: key on the integer content index.
  // Array position of each CONTENT element; whitespace's shared `-1` is skipped,
  // so it never overwrites a real content position.
  if (coveredIndices.length === 0) return [];
  const positionByContentIndex = new Map<number, number>();
  transcriptionElements.forEach((el, pos) => {
    if (el.initial_index >= 0) {
      positionByContentIndex.set(el.initial_index, pos);
    }
  });
  for (const idx of coveredIndices) {
    const pos = positionByContentIndex.get(idx);
    if (pos === undefined) return null;
    if (pos < first) first = pos;
    if (pos > last) last = pos;
  }
  return transcriptionElements.slice(first, last + 1);
}

/**
 * PLAT-10266 (Card 7, §D) — the authored-override RE-KEY ids for a VANISHED
 * covered span, or `undefined` for a normal span. When `collectSpanSourceElements`
 * can still place the span there is nothing to re-anchor (normal path); when it
 * returns `null` AND a baseline is present, resolve the replacement's added-token
 * run at the load-time slot and return those tokens' fresh ids so the authored
 * override renders by identity instead of re-orphaning. Shared by `confirm` and
 * `confirmDirected` so both createOverrideSegment call sites re-key identically.
 */
function resolveReanchorIds(
  sourceBaseline: ReadonlyMap<string, SourceBaselineEntry> | undefined,
  transcriptionElements: readonly TranscriptElement[],
  coveredElementIds: readonly string[],
  coveredIndices: readonly number[],
): string[] | undefined {
  if (!sourceBaseline) return undefined;
  if (
    collectSpanSourceElements(
      transcriptionElements,
      coveredElementIds,
      coveredIndices,
    ) !== null
  ) {
    return undefined;
  }
  return resolveVanishedSpanSource(
    sourceBaseline,
    transcriptionElements,
    coveredElementIds,
    coveredIndices,
  )?.freshElementIds;
}

/**
 * Widen a covered set to the WHOLE segment(s) it touches — every ACTIVE fragment
 * sharing a covered fragment's `parentId` — in source order.
 *
 * Segment view (the workspace default) renders ONE word per SEGMENT, stamped
 * with the segment's lead source index only, so the selection resolver returns
 * just the lead FRAGMENT. Confirming that would overwrite the first fragment
 * with the whole-segment replacement and leave the rest stale (a garbled save).
 * Paragraph and Fragment views render one word per phrase/fragment and already
 * resolve correctly, so they are NOT expanded (a single phrase is fixable
 * there). Shared by the selection path and `openSpanForIndices`, so every
 * open path widens the same way.
 */
function expandToWholeSegments(
  coveredPhrases: readonly TranslationPhrase[],
  activePhrases: readonly TranslationPhrase[],
): TranslationPhrase[] {
  const activeByParent = new Map<string, TranslationPhrase[]>();
  for (const p of activePhrases) {
    if (p.parentId === undefined) continue;
    const list = activeByParent.get(p.parentId);
    if (list) list.push(p);
    else activeByParent.set(p.parentId, [p]);
  }
  const expanded = new Set<TranslationPhrase>(coveredPhrases);
  for (const p of coveredPhrases) {
    if (p.parentId === undefined) continue;
    for (const sib of activeByParent.get(p.parentId) ?? []) {
      expanded.add(sib);
    }
  }
  return [...expanded].sort(
    (a, b) => Math.min(...a.originalIndices) - Math.min(...b.originalIndices),
  );
}

/**
 * Is this span's write wider than what the reviewer actually highlighted?
 *
 * True only in Segment view, and only when the selection did not already fill
 * its segment. A span re-editing a STORED override is excluded: its value is
 * byte-exact from the backend (join spacing included) and its fragments need not
 * re-join to it, so splicing into it could silently change text outside the
 * selection. Those spans keep the pre-PLAT-11648 whole-span editor.
 */
function isWidenedBeyondSelection(
  model: SpanDisplayModel,
  selectedIndices: readonly number[],
): boolean {
  if (model.existingOverrideSegmentId) return false;
  return selectedIndices.length < model.coveredIndices.length;
}

/**
 * Put `value` back where the selection was: the covered fragments the selection
 * did NOT touch, in source order, with `value` standing in for the run it did.
 *
 * This is the inverse of the widening. The reviewer edits only their words, and
 * the span that gets WRITTEN is still the whole widened run — so a correction
 * can never leave half a segment stale, which is the reason the widening exists.
 *
 * `removeSpan` is this with an empty `value`: the selected run is replaced by
 * nothing and the surrounding fragments close up.
 *
 * NO exception lives in here. This function always splices — including for a
 * span re-editing a STORED correction, where Remove must delete the highlighted
 * fragment out of the correction rather than blank the whole of it. The one
 * caller that must NOT splice is `confirm` on such a span, and it says so at its
 * own call site (`confirmValueForSpan`), where the reader can see it.
 */
function spliceSelectedValue(span: PendingSpan, value: string): string {
  const selected = new Set(span.selectedIndices);
  const parts: string[] = [];
  let placed = false;
  for (const phrase of span.model.orderedPhrases) {
    if (phrase.originalIndices.some(i => selected.has(i))) {
      // The selected run is contiguous in source order, so one insertion at the
      // first selected fragment puts the text back exactly where it came from.
      if (!placed) {
        parts.push(value);
        placed = true;
      }
      continue;
    }
    parts.push(phrase.translatedValue ?? '');
  }
  // No covered fragment carried a selected index (nothing was widened, or the
  // selection resolved to the whole run) — the value IS the span.
  if (!placed) return value;
  return joinPhraseValues(
    parts.filter(v => v.trim().length > 0).map(v => v.trim()),
  );
}

/**
 * The value `confirm` authors for `value` typed into the editing box.
 *
 * EXCEPTION, named here rather than buried in the splice: when the span
 * re-edits a STORED correction the box already held the WHOLE correction
 * (`isWidenedBeyondSelection` returns false for those spans, so the editor is
 * not narrowed to the selection), which makes the typed text the complete
 * replacement. Splicing it would keep the unselected fragments AND re-insert
 * them as part of `value` — for a correction that "Split into Words" left as
 * `hola` + `mundo`, highlighting `mundo` and typing `hola a todos` would store
 * `hola hola a todos` (PLAT-11648).
 *
 * `removeSpan` has no box, so it never takes this exception: it splices in
 * every span shape (pull request #3566 review).
 */
function confirmValueForSpan(span: PendingSpan, value: string): string {
  if (span.model.existingOverrideSegmentId) return value;
  // PLAT-11648 — the box held only the highlighted words, so put them back into
  // the covered run before authoring. Where nothing was widened the splice
  // returns `value` untouched.
  return spliceSelectedValue(span, value);
}

/** The distinct dub lines (period-level `parentId`s) a span covers. */
function segmentIdsOf(span: PendingSpan): string[] {
  return [
    ...new Set(
      span.model.orderedPhrases
        .map(p => p.parentId)
        .filter((id): id is string => !!id),
    ),
  ];
}

export function useSpanReplace({
  enabled,
  language,
  viewMode,
  translatedColumnRef,
  originalColumnRef,
  fragmenter,
  onSpanConfirmed,
  onRequestReDub,
  sourceBaseline,
  staleLeads,
  onStaleSelectionChange,
}: UseSpanReplaceParams): SpanReplaceController {
  const translationStore = useTranslationStore();
  const rootStore = useStore();

  // The host-scoped source elements — the SAME array the translated view renders
  // from (`getFinalTranscriptionElements()` returns the store's
  // `transcriptionElements`).
  const transcriptionElements =
    rootStore.transcriptionStore?.transcriptionElements ?? [];

  // Source elements keyed by `initial_index` — the cross-speaker guard's label
  // lookup, and the editor's "Original text" fallback for inline hosts (no
  // original pane to read). Memoized on the elements ref (stable until a
  // transcript reload), so this O(N) build runs once per reload, not per render.
  const sourceElementByIndex = useMemo(() => {
    const map = new Map<number, TranscriptElement>();
    for (const el of transcriptionElements) map.set(el.initial_index, el);
    return map;
  }, [transcriptionElements]);

  const [pendingSpan, setPendingSpan] = useState<PendingSpan | null>(null);
  const [editor, setEditor] = useState<PendingSpan | null>(null);

  // Latest editor state for the listener (which is registered once) so it can
  // suspend selection-tracking while the editor is open (the editor's textarea
  // selection must not re-trigger the pending span).
  const editorRef = useRef<PendingSpan | null>(null);
  editorRef.current = editor;

  // Derive the full `PendingSpan` for a set of covered ACTIVE phrases — the
  // display model, the D10 overlap / cross-speaker guards, and the
  // original-translation + attribution rows. Shared by BOTH open paths
  // (PLAT-10257 A4): the DOM-selection resolver below and `openForIndices`
  // (the coverage-popover entry), so the guard logic exists exactly once and
  // is always computed from the CURRENT store data at open time.
  const buildPendingSpan = useCallback(
    (
      coveredPhrases: TranslationPhrase[],
      translation: {
        phrases: TranslationPhrase[];
        parentPhrases?: ParentPhrase[];
      },
      anchorElement: HTMLElement,
      // PLAT-11340 follow-up — the phrases the selection covered BEFORE Segment
      // view widened it. Omitted by callers that never widen, which then get
      // the covered set (selection == span).
      selectedPhrases?: readonly TranslationPhrase[],
    ): PendingSpan => {
      const segmentsById = new Map<string, ParentPhrase>();
      for (const seg of translation.parentPhrases ?? []) {
        segmentsById.set(seg.id, seg);
      }
      const builtModel = buildSpanDisplayModel(coveredPhrases, segmentsById);
      // The editor's "Original text" comes from each covered phrase's
      // `originalValueSnapshot`, but that field is only populated on override
      // fragments — plain machine-translation phrases carry an empty string, so
      // the join is blank. Fall back to the SOURCE words rendered in the original
      // (left) pane for the covered indices, scoped to that pane (CLAUDE.md #12).
      const model: SpanDisplayModel = builtModel.originalSnapshot
        ? builtModel
        : {
            ...builtModel,
            // Prefer the original PANE text (the 3-pane workspace renders it);
            // fall back to the source elements straight from the store for inline
            // hosts, which pass a null `originalColumnRef` and so have no pane to
            // read — without this the inline editor's "Original text" is blank.
            originalSnapshot:
              readOriginalPaneText(
                originalColumnRef.current,
                builtModel.coveredIndices,
                builtModel.coveredElementIds,
              ) ||
              readSourceTextFromElements(
                sourceElementByIndex,
                builtModel.coveredIndices,
              ),
          };

      // Epic D10 no-nesting rule. A span may RE-EDIT one existing override — that
      // case sets `existingOverrideSegmentId` (the span sits entirely within a
      // single override segment) and routes confirm to `updateOverrideSegment`. A
      // span that PARTIALLY overlaps an override (touches it and extends beyond)
      // or spans TWO must never be NESTED over them (that collides their ids +
      // breaks one-click undo). Confirm ABSORBS those corrections into the new
      // one instead (`absorbIntoOverrideSegment`): their ids ride the pending
      // span so the editor can say how many, and so Revert can drop them. An
      // override is any `o_…`-id / manual / directed parent (the same rule the
      // store op applies). `createOverrideSegment` still rejects a nest as the
      // backstop for callers that do not go through the absorb path.
      const overlappingOverrideSegmentIds = model.existingOverrideSegmentId
        ? []
        : overrideSegmentIdsTouchedBy(coveredPhrases, segmentsById);
      const overlapsOverride = overlappingOverrideSegmentIds.length > 0;
      // Cross-SPEAKER guard (PLAT-10945). Read off the hook's OWN
      // `sourceElementByIndex` — NOT a global store. Two of the three hosts that
      // switch this gesture on (the clip-preview modal and the Content Studio
      // Transcript & Speakers tab) wrap the tree in a proxied root store whose
      // `transcriptionElements` getter returns that host's own array
      // (`clipTranscriptProxies.ts:48-51`), so reading globally would compare
      // labels from a different transcript than the one on screen in two hosts out
      // of three. Reading the hook's map inherits the host scope for free.
      // Only labels that are SET are compared: `speaker_label` is optional and
      // whitespace carries none, so comparing unset values would refuse
      // legitimate one-speaker selections. Punctuation DOES carry a label, and
      // it counts here like a word's. The writers keep marks and words on one
      // label (PLAT-11670, `@src/utility/speakerRelabel.ts`) — but ONLY for a
      // passage written since PLAT-11670. There is no repair on load and no
      // migration, so a passage reassigned before it still holds the old shape
      // (words moved, marks on the previous speaker) until someone reassigns or
      // undoes over it again, and this guard still refuses there. Before
      // blaming the selection logic for a refusal, read the stored labels.
      // Every covered source index is
      // checked, not just each phrase's lead, so a phrase that itself straddles a
      // speaker change is caught too.
      const speakersCovered = new Set(
        model.coveredIndices
          .map(i => sourceElementByIndex.get(i)?.speaker_label)
          .filter((label): label is string => !!label),
      );
      // The speaker crossing is the only refusal left. A PARAGRAPH crossing is
      // deliberately NOT a reason (PLAT-10945): the refusal that used to sit here
      // named a boundary the reviewer cannot see — no paragraphs are drawn on the
      // captions screen at all — and it fired inside a single untouched caption
      // row. One correction over words from two paragraphs is now the supported
      // case; the write path has no paragraph parameter, so it is the same call
      // with a longer list. An override overlap is not a reason either since
      // PLAT-11655 (absorbed on Confirm, see above).
      const blockedReason: SpanBlockReason | undefined =
        speakersCovered.size > 1 ? 'spans-multiple-speakers' : undefined;
      // The machine "original translation" for the editor's read-only row + (on a
      // re-edit) the revert baseline (PLAT-10265). Re-edit: the retired fragments
      // this override superseded carry the untouched machine text. Absorb span
      // (PLAT-11655): a covered fragment that belongs to an overlapped correction
      // stands in for the machine originals that correction retired over the
      // same source words — the fragment's own value is the CORRECTION, not the
      // machine text. Fresh span: each covered phrase's captured machine
      // baseline — its pre-edit value, or its current value when never
      // user-edited.
      const overlappingSet = new Set(overlappingOverrideSegmentIds);
      const machineOriginalsOf = (
        p: TranslationPhrase,
      ): TranslationPhrase[] => {
        if (!p.parentId || !overlappingSet.has(p.parentId)) return [p];
        const owned = new Set(p.originalIndices);
        return translation.phrases.filter(
          r =>
            r.supersededBy === p.parentId &&
            r.originalIndices.some(i => owned.has(i)),
        );
      };
      const originalTranslatedValue = model.existingOverrideSegmentId
        ? joinFragments(
            translation.phrases.filter(
              p => p.supersededBy === model.existingOverrideSegmentId,
            ),
          ) || undefined
        : joinFragments(
            [...new Set(coveredPhrases.flatMap(machineOriginalsOf))],
            p => p.originalTranslatedValue ?? p.translatedValue,
          ) || undefined;
      // Who last applied this change (PLAT-10444): a re-edit reads the override
      // segment's attribution; a single-phrase fix reads the covered phrase's
      // (most recent wins). Undefined ⇒ fresh span / pre-attribution edit.
      const editedBy = model.existingOverrideSegmentId
        ? segmentsById.get(model.existingOverrideSegmentId)?.editedBy
        : coveredPhrases
            .map(p => p.editedBy)
            .filter((e): e is NonNullable<TranslationPhrase['editedBy']> => !!e)
            .sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''))[0];
      const selectedIndices = unionOriginalIndices(
        selectedPhrases ?? coveredPhrases,
      );
      return {
        anchorElement,
        model,
        originalTranslatedValue,
        blockedReason,
        speakerLabels: [...speakersCovered],
        // Only when there is something to absorb, and never on a refusal: a
        // cross-speaker block never reaches Confirm, so naming corrections it
        // would absorb would describe an action that cannot happen.
        ...(overlapsOverride && !blockedReason
          ? { overlappingOverrideSegmentIds }
          : {}),
        editedBy,
        selectedIndices,
        // PLAT-11648 — the box holds what was highlighted. Where nothing was
        // widened this IS the whole span, so every un-widened view is byte-for-
        // byte unchanged.
        editableValue: isWidenedBeyondSelection(model, selectedIndices)
          ? joinFragments(selectedPhrases ?? coveredPhrases)
          : model.currentTranslatedValue,
        replacingWordCount: isWidenedBeyondSelection(model, selectedIndices)
          ? selectedIndices.length
          : model.coveredIndices.length,
      };
    },
    [originalColumnRef, sourceElementByIndex],
  );

  // Resolve the live selection to a pending span (or clear it). Scoped to the
  // translated column ref (CLAUDE.md #12). Builds the display model from the
  // ACTIVE fragments + the segment records (so a span fully inside one
  // manual/directed segment routes confirm to `updateOverrideSegment`).
  // PLAT-11650 — true from the second press of a double-click until that
  // press is released: the word the browser selects on that press is the
  // gesture's own, not a span the reviewer drew, and must not resolve into
  // the Fix / Remove Translation trigger even for a frame. Read by
  // `resolvePending`; written by the mouse listeners below.
  const doubleClickPressRef = useRef(false);

  /**
   * Resolve the live DOM selection in the translated column to the phrases it
   * covers and the word to anchor on — pure read, no state. Null when there is
   * no column, no translation, or the selection covers nothing of this
   * translation. `resolvePending` builds the trigger's state from it; the
   * context menu's Re-dub reads it at the moment of the click (PLAT-10833),
   * since the debounced state can be stale or withheld (an editable focused
   * at resolve time — the Co-Producer box on a fresh open — keeps the trigger
   * away, and must not keep a deliberate menu action away too).
   */
  const resolveSelectionSpan = useCallback((): {
    translation: NonNullable<
      ReturnType<typeof translationStore.getTranslation>
    >;
    coveredPhrases: TranslationPhrase[];
    resolvedPhrases: TranslationPhrase[];
    anchorElement: HTMLElement;
  } | null => {
    const container = translatedColumnRef.current;
    const translation = translationStore.getTranslation(language);
    if (!container || !translation) return null;
    const activePhrases = translation.phrases.filter(p => p.active !== false);
    const resolution: SelectionResolution = resolveTranslatedSelectionPhrases(
      container,
      activePhrases,
    );
    if (!resolution.ok) return null;
    // The trigger appears for ANY resolved selection (≥1 covered fragment).
    // This workspace's aligned view has NO separate single-phrase editor — the
    // only way to fix a translation here is this affordance — so it must cover
    // single-phrase fixes too. Without this, selecting one phrase (a fragment
    // that maps to a single source word) showed nothing and the phrase was
    // uneditable. On confirm, `createOverrideSegment` delegates a ≤1-fragment
    // manual span to `editTranslatedPhrase` (the AC's single-phrase store path),
    // so a lone phrase edits in place — no override segment is created.
    // (Relaxes the original "≥2 source words" gate, which assumed a right-click
    // single-phrase editor this surface does not have — PLAT-10214 follow-up.)

    const coveredPhrases: TranslationPhrase[] =
      viewMode === 'segment'
        ? expandToWholeSegments(resolution.phrases, activePhrases)
        : resolution.phrases;
    const coveredIndices = [
      ...new Set(coveredPhrases.flatMap(p => p.originalIndices)),
    ].sort((a, b) => a - b);
    // PLAT-10611 (F5) — the covered content words' stable ids, source order, for
    // the id-first anchor lookup (R15) below.
    const coveredElementIds = [
      ...new Set(coveredPhrases.flatMap(p => p.originalElementIds ?? [])),
    ];

    // Anchor the trigger/editor at the selection's first translated word — its
    // VISUAL start. Translated word order ≠ source order, so the lowest source
    // index (`coveredIndices[0]`) can map to a word elsewhere on screen; and when
    // that index isn't rendered here, the old `?? container` fallback pinned the
    // pill to the column's left edge. Read the live selection's start node instead
    // (the browser normalises start ≤ end in document order, so this is correct for
    // right-to-left drags too); fall back to the source-index lookup, then the
    // container, only if that can't be resolved.
    const selection = container.ownerDocument.getSelection();
    const selectionRange =
      selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const selectionStartNode = selectionRange?.startContainer;
    const selectionStartEl =
      selectionStartNode && container.contains(selectionStartNode)
        ? ((selectionStartNode instanceof HTMLElement
            ? selectionStartNode
            : selectionStartNode.parentElement
          )?.closest<HTMLElement>('[data-initial-index]') ?? null)
        : null;
    const anchorElement =
      selectionStartEl ??
      // PLAT-10611 (F5, R15) — anchor by the covered set's lead stable id FIRST
      // (drift-correct), fall back to the integer `coveredIndices[0]`.
      (coveredElementIds[0]
        ? container.querySelector<HTMLElement>(
            `[data-source-uuid='${coveredElementIds[0]}']`,
          )
        : null) ??
      container.querySelector<HTMLElement>(
        `[data-initial-index='${coveredIndices[0]}']`,
      ) ??
      container;
    return {
      translation,
      coveredPhrases,
      resolvedPhrases: resolution.phrases,
      anchorElement,
    };
  }, [translatedColumnRef, translationStore, language, viewMode]);

  const resolvePending = useCallback(() => {
    if (editorRef.current) return; // editor open — ignore textarea selection
    if (doubleClickPressRef.current) {
      setPendingSpan(null);
      onStaleSelectionChange?.(null);
      return;
    }
    // PLAT-11650 — a drag inside the in-place phrase field is typing, not a
    // span selection: the Fix Translation trigger must not appear over it. And
    // a span resolved just BEFORE the field took focus — the word the
    // double-click that opened it selected — must go, not stay up over the
    // field, so this clears rather than merely declining to resolve.
    if (isEditableElementFocused()) {
      setPendingSpan(null);
      onStaleSelectionChange?.(null);
      return;
    }
    const resolved = resolveSelectionSpan();
    if (!resolved) {
      setPendingSpan(null);
      // Card R2 — clear the stale-selection signal on every path that clears the span,
      // or the host's right-click routing keeps acting on a selection that is gone.
      onStaleSelectionChange?.(null);
      return;
    }
    const { translation, coveredPhrases, resolvedPhrases, anchorElement } =
      resolved;

    // PLAT-10266 (Card R2) — a selection that is exactly ONE stale fragment and nothing
    // else belongs to the RESOLVE menu, not the span path. Decided HERE, after the
    // Segment-view widening above, because Denys ruled that stale-vs-mixed is judged on
    // the widened set. `singleStaleLeadOfSelection` is the same predicate the right-click
    // path consults, so the two triggers cannot disagree; it returns null for a mixed
    // selection (span path wins, PLAT-10467 stands), for several stale fragments (Card R3
    // widens the menu to a set first), and whenever no `staleLeads` was supplied at all —
    // which is what leaves this hook's two other consumers untouched.
    const staleLead = singleStaleLeadOfSelection(coveredPhrases, staleLeads);
    if (staleLead !== null && onStaleSelectionChange) {
      // Suppress the span affordance rather than render both: the reviewer must be
      // offered one route, and the resolve menu is the correct one for a stale fragment.
      setPendingSpan(null);
      onStaleSelectionChange({ lead: staleLead, anchorElement });
      return;
    }
    onStaleSelectionChange?.(null);
    setPendingSpan(
      buildPendingSpan(
        coveredPhrases,
        translation,
        anchorElement,
        resolvedPhrases,
      ),
    );
  }, [
    resolveSelectionSpan,
    buildPendingSpan,
    staleLeads,
    onStaleSelectionChange,
  ]);

  useEffect(() => {
    if (!enabled) {
      setPendingSpan(null);
      return;
    }
    // `selectionchange` fires on the document; we re-resolve scoped to our
    // column. Debounced to the next frame so a drag-select doesn't thrash.
    let raf: number | null = null;
    const onSelectionChange = () => {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = null;
        resolvePending();
      });
    };
    // The second press of a double-click (`detail` 2) is known BEFORE the
    // browser selects the word and fires `selectionchange`, so the flag is up
    // in time. It comes down after the release — on the next task, so the
    // `dblclick` that follows the release (and may open the in-place field)
    // has run — and the selection is re-read: a double-click that opened a
    // field finds an editable focused and stays clear; a double-click-and-drag
    // that drew a real span resolves it then.
    const inColumn = (e: MouseEvent) =>
      e.target instanceof Node &&
      !!translatedColumnRef.current?.contains(e.target);
    const onMouseDown = (e: MouseEvent) => {
      if (!inColumn(e)) return;
      doubleClickPressRef.current = e.detail === 2;
    };
    let release: ReturnType<typeof setTimeout> | null = null;
    const onMouseUp = (e: MouseEvent) => {
      if (!doubleClickPressRef.current || !inColumn(e)) return;
      release = setTimeout(() => {
        release = null;
        doubleClickPressRef.current = false;
        resolvePending();
      }, 0);
    };
    document.addEventListener('selectionchange', onSelectionChange);
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('mouseup', onMouseUp, true);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      if (release !== null) clearTimeout(release);
      doubleClickPressRef.current = false;
      document.removeEventListener('selectionchange', onSelectionChange);
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('mouseup', onMouseUp, true);
    };
  }, [enabled, resolvePending, translatedColumnRef]);

  // Paint / clear the original-side lock as the editor opens / closes.
  useEffect(() => {
    const original = originalColumnRef.current;
    if (!original) return;
    if (editor) {
      applySpanReplaceLock(original, new Set(editor.model.coveredIndices));
    } else {
      clearSpanReplaceLock(original);
    }
    return () => clearSpanReplaceLock(original);
  }, [editor, originalColumnRef]);

  const openEditor = useCallback(() => {
    if (!pendingSpan) return;
    setEditor(pendingSpan);
  }, [pendingSpan]);

  // PLAT-10257 (A4) — the coverage-popover entry: open the editor for an
  // EXPLICIT covered-index span, no DOM selection. The indices are
  // authoritative (the popover entry's span — the same span its Undo reverts),
  // so no segment-view expansion applies. `buildPendingSpan` computes the
  // guards (overlap via `spanTouchesOverride`, cross-speaker) and the
  // re-edit signal from the CURRENT store data, so a popover-opened span that
  // touches an existing fix renders the notice instead of reaching the op and
  // getting a silent null. Sets pendingSpan + editor together — the exact
  // state `openEditor` leaves behind, no second editor-state mechanism.
  const openSpanForIndices = useCallback(
    (
      coveredIndices: readonly number[],
      { widenToSegments }: { widenToSegments: boolean },
    ) => {
      const translation = translationStore.getTranslation(language);
      const covered = new Set(coveredIndices);
      const activePhrases = (translation?.phrases ?? []).filter(
        p => p.active !== false,
      );
      const resolved = activePhrases.filter(p =>
        p.originalIndices.some(i => covered.has(i)),
      );
      const coveredPhrases = widenToSegments
        ? expandToWholeSegments(resolved, activePhrases)
        : resolved;
      if (!translation || coveredPhrases.length === 0) {
        // Defensive floor — the popover only offers the action for a live
        // coverage entry, so covered phrases always exist there. Loud, never
        // silent (the silent no-op is the defect this entry point guards).
        logger.warn(
          'span-replace openForIndices: no active phrase covers the indices',
          { language, coveredIndices },
        );
        return;
      }
      const container = translatedColumnRef.current;
      // Anchor at the covered set's lead word — the id-first chain the
      // selection path uses, minus the selection-start (there is none here).
      const coveredElementIds = [
        ...new Set(coveredPhrases.flatMap(p => p.originalElementIds ?? [])),
      ];
      const leadIndex = [
        ...new Set(coveredPhrases.flatMap(p => p.originalIndices)),
      ].sort((a, b) => a - b)[0];
      const anchorElement =
        (coveredElementIds[0]
          ? container?.querySelector<HTMLElement>(
              `[data-source-uuid='${coveredElementIds[0]}']`,
            )
          : null) ??
        container?.querySelector<HTMLElement>(
          `[data-initial-index='${leadIndex}']`,
        ) ??
        container ??
        // Degenerate fallback (no translated pane mounted): still open the
        // editor rather than doing nothing — never a silent no-op.
        document.body;

      const span = buildPendingSpan(
        coveredPhrases,
        translation,
        anchorElement,
        resolved,
      );
      setPendingSpan(span);
      setEditor(span);
    },
    [translationStore, language, translatedColumnRef, buildPendingSpan],
  );

  const openForIndices = useCallback(
    (coveredIndices: readonly number[]) => {
      // The popover's indices are authoritative — they ARE the entry's span —
      // so no segment-view widening applies here.
      openSpanForIndices(coveredIndices, { widenToSegments: false });
    },
    [openSpanForIndices],
  );

  // The selection's distinct covered period-level segment ids (PLAT-10593 dub
  // units = phrases grouped by `parentId`) — the `segmentIds` the dub engine
  // keys on. A phrase without a `parentId` (the derived `solo:` fallback) can't
  // be span-scoped, so it's dropped; with the persisted `p_` chain (PLAT-10618)
  // every sentence carries one.
  const reDubSegmentIds = useMemo(
    () => (pendingSpan ? segmentIdsOf(pendingSpan) : []),
    [pendingSpan],
  );

  const canReDub = !!onRequestReDub && reDubSegmentIds.length > 0;

  /** Hand a span's dub lines to the host and drop the affordance. */
  const sendReDub = useCallback(
    (span: PendingSpan): boolean => {
      const segmentIds = segmentIdsOf(span);
      if (!onRequestReDub || segmentIds.length === 0) return false;
      // The anchor is the selection's first word, a DOM node that outlives the
      // affordance; read before the span is dropped.
      const request: ReDubSelectionRequest = {
        segmentIds,
        speakerLabels: span.speakerLabels,
        anchorElement: span.anchorElement,
        translatedText: span.model.currentTranslatedValue,
        sourceText: span.model.originalSnapshot,
      };
      // Dismiss the affordance + drop the selection (mirrors `cancel`) so the menu
      // doesn't linger behind the popover, then hand the selection to the host.
      setPendingSpan(null);
      clearSpanReplaceLock(originalColumnRef.current);
      if (typeof window !== 'undefined') {
        window.getSelection()?.removeAllRanges();
      }
      onRequestReDub(request);
      return true;
    },
    [onRequestReDub, originalColumnRef],
  );

  const reDub = useCallback(() => {
    if (pendingSpan) sendReDub(pendingSpan);
  }, [pendingSpan, sendReDub]);

  // PLAT-10833 — the context menu's path: the span is the pending one when the
  // trigger's resolve got there, else the live selection resolved right now.
  const spanForSelection = useCallback((): PendingSpan | null => {
    if (pendingSpan) return pendingSpan;
    const resolved = resolveSelectionSpan();
    if (!resolved) return null;
    return buildPendingSpan(
      resolved.coveredPhrases,
      resolved.translation,
      resolved.anchorElement,
      resolved.resolvedPhrases,
    );
  }, [pendingSpan, resolveSelectionSpan, buildPendingSpan]);

  const canReDubSelection = useCallback((): boolean => {
    if (!onRequestReDub) return false;
    const span = spanForSelection();
    return !!span && segmentIdsOf(span).length > 0;
  }, [onRequestReDub, spanForSelection]);

  const reDubSelection = useCallback((): boolean => {
    const span = spanForSelection();
    return !!span && sendReDub(span);
  }, [spanForSelection, sendReDub]);

  const cancel = useCallback(() => {
    setEditor(null);
    setPendingSpan(null);
    clearSpanReplaceLock(originalColumnRef.current);
    // Drop the DOM selection so the trigger doesn't immediately re-resolve.
    if (typeof window !== 'undefined') {
      window.getSelection()?.removeAllRanges();
    }
  }, [originalColumnRef]);

  /**
   * Author `value` over `current`'s covered run — the shared body of `confirm`
   * (a typed correction) and `removeSpan` (the same write with an empty value).
   * Extracted rather than duplicated: the two differ ONLY in where the span
   * comes from and what is written, and every guard, re-edit branch, analytics
   * call and post-write flush below has to hold identically for both, or a
   * removal becomes the one write that can nest inside an existing override.
   */
  const authorSpanValue = useCallback(
    async (current: PendingSpan, value: string) => {
      // Backstop for the refusal: the editor hides Confirm when blocked (a
      // cross-speaker span), but never author over a blocked span even if
      // confirm is reached some other way.
      if (current.blockedReason) {
        logger.info('span-replace write ignored — span is blocked', {
          reason: current.blockedReason,
          language,
        });
        return;
      }
      const { model } = current;
      const wordCount = model.coveredIndices.length;
      // Close the editor + clear the lock immediately (optimistic) — the store
      // op is async (it may call the splitter + save).
      setEditor(null);
      setPendingSpan(null);
      clearSpanReplaceLock(originalColumnRef.current);
      if (typeof window !== 'undefined') {
        window.getSelection()?.removeAllRanges();
      }

      try {
        if (model.existingOverrideSegmentId) {
          // The span is fully inside one manual/directed segment → re-edit the
          // whole segment + re-fragment (D10 re-edit). Undo still reaches the
          // machine original (the retired originals are untouched).
          const ok = await translationStore.updateOverrideSegment(
            language,
            model.existingOverrideSegmentId,
            value,
            { fragment: !!fragmenter, fragmenter },
          );
          trackTranslationReview_SpanReplaced({
            story_id: rootStore.storyId ?? undefined,
            language,
            word_count: wordCount,
            // Re-edit doesn't change the covered-original count; report the
            // covered span size as the fragment count proxy (fine-split count
            // is derived async). `created_override_segment` is false — it
            // updated an existing one.
            fragment_count: wordCount,
            created_override_segment: false,
          });
          if (ok) {
            // PLAT-10270 (SPEC criterion 6 / item b): a confirmed fix lands as
            // ONE coarse override piece — NO auto-split on confirm. The reviewer
            // opts into fine-splitting via the global "Split into Words" action.
            // `resplitOverrideSegment` stays for the per-segment Split control.
            // T8 — the re-edit persisted; kick off the coverage check for the
            // span. Fire-and-forget (not awaited) so it never blocks the save.
            onSpanConfirmed?.({
              lang: language,
              coveredElementIds: model.coveredElementIds,
            });
          } else {
            logger.warn('span-replace re-edit: unknown segment', {
              language,
              segmentId: model.existingOverrideSegmentId,
            });
          }
          return;
        }

        const indices = unionOriginalIndices(model.orderedPhrases);

        if (current.overlappingOverrideSegmentIds?.length) {
          // PLAT-11655 — the span overlaps existing corrections: absorb them
          // into the new one in ONE store write. `value` is already spliced
          // over the COVERED run (PLAT-11648: the covered-but-unselected
          // fragments Segment view widened onto are back in place around the
          // typed text). The store adds only the absorbed corrections'
          // fragments OUTSIDE the covered run — a fragment is either covered
          // (kept by the splice here) or not (kept by the store), never both,
          // so no text is composed twice. The store also widens the coverage
          // to the union span, re-points the corrections' retired machine
          // originals at the new id (one undo still reaches the machine text
          // everywhere) and removes the absorbed records.
          const absorbedId = await translationStore.absorbIntoOverrideSegment(
            language,
            indices,
            value,
            {
              fragment: !!fragmenter,
              fragmenter,
              coveredElementIds: model.coveredElementIds,
            },
          );
          trackTranslationReview_SpanReplaced({
            story_id: rootStore.storyId ?? undefined,
            language,
            word_count: wordCount,
            fragment_count: wordCount,
            created_override_segment: absorbedId !== null,
          });
          if (absorbedId === null) {
            logger.warn('span-replace absorb authored nothing', {
              language,
              overlapping: current.overlappingOverrideSegmentIds,
            });
            // Not silently: the editor was closed optimistically before the
            // store op, so a bare return would look like the typed text was
            // accepted and then disappeared. Re-open the span as a refusal —
            // the same warning panel a cross-speaker span gets — which states
            // that nothing was changed (pull request #3566 review).
            setEditor({ ...current, blockedReason: 'absorb-wrote-nothing' });
            return;
          }
          // Same post-write contract as a fresh create: ONE coarse piece, no
          // auto-split (PLAT-10270 item b); coverage kick fire-and-forget.
          onSpanConfirmed?.({
            lang: language,
            coveredElementIds: model.coveredElementIds,
          });
          return;
        }

        // Fresh override over the covered run. `createOverrideSegment` delegates
        // a ≤1-fragment manual span to `editTranslatedPhrase`; the trigger only
        // appears for ≥2 source words, but the union may still resolve to one
        // fragment (a multi-word fragment) — the store handles that.
        // Card 7 (§D) — when the covered source span VANISHED (a "Replace Term"
        // deleted the covered token), re-key the authored override to the
        // replacement's fresh added-token ids so it renders by identity instead
        // of re-orphaning. The typed `value` is authored unchanged; only the
        // AUTHORED coverage is re-anchored. Undefined on the normal path.
        const reanchorAuthoredElementIds = resolveReanchorIds(
          sourceBaseline,
          transcriptionElements,
          model.coveredElementIds,
          model.coveredIndices,
        );
        const createdId = await translationStore.createOverrideSegment(
          language,
          indices,
          value,
          {
            fragment: !!fragmenter,
            fragmenter,
            // PLAT-10611 (F5, F4-carry) — the covered stable ids so the store's
            // covered-set intersection matches by identity FIRST (drift-correct),
            // integer fallback for id-less whitespace / legacy blobs.
            coveredElementIds: model.coveredElementIds,
            reanchorAuthoredElementIds,
          },
        );
        trackTranslationReview_SpanReplaced({
          story_id: rootStore.storyId ?? undefined,
          language,
          word_count: wordCount,
          fragment_count: wordCount,
          created_override_segment: createdId !== null,
        });
        // createdId === null ⇒ a ≤1-fragment span delegated to
        // editTranslatedPhrase, which saves on a ~2s debounce. The reviewer sees
        // the editor close as a commit, so closing the workspace within that
        // window would silently drop the edit. Flush so the single-phrase path
        // matches the override path's immediate-save guarantee.
        // PLAT-10270 (SPEC criterion 6 / item b): a non-null `createdId` is a
        // fresh coarse override — no auto-split on confirm; it stays one piece
        // until the reviewer runs the global "Split into Words" action.
        if (createdId === null) {
          await translationStore.flushChanges();
        }
        // T8 — the fresh override persisted; kick off the coverage check for the
        // span. Fire-and-forget (not awaited) so it never blocks the save.
        onSpanConfirmed?.({
          lang: language,
          coveredElementIds: model.coveredElementIds,
        });
      } catch (error) {
        logger.error('span-replace confirm failed', {
          language,
          error,
        });
      }
    },
    [
      translationStore,
      language,
      fragmenter,
      onSpanConfirmed,
      originalColumnRef,
      rootStore,
      transcriptionElements,
      sourceBaseline,
    ],
  );

  const confirm = useCallback(
    async (value: string) => {
      if (!editor) return;
      await authorSpanValue(editor, confirmValueForSpan(editor, value));
    },
    [editor, authorSpanValue],
  );

  /**
   * PLAT-11340 follow-up — "Remove Translation" on the selected span.
   *
   * A removal is not its own storage shape: it is `authorSpanValue` with an
   * empty string, so it lands as an ordinary reversible correction and inherits
   * the whole existing machinery — the changelog entry, Revert, the
   * changed-phrase mark, `DubCoverageService`'s out-of-date check, and
   * `srtExport`'s empty-value skip. Only the SOURCE is left alone: the words
   * keep playing, and just their translation goes.
   *
   * Unlike `confirm` this runs from the trigger, where no editor is open — so
   * it takes `editor ?? pendingSpan`.
   */
  const removeSpan = useCallback(async () => {
    const current = editor ?? pendingSpan;
    if (!current) return;
    // Write the span back WITHOUT the selected words, rather than blanking it.
    //
    // The write lands on `model.coveredIndices`, which in Segment view is the
    // whole segment (`resolvePending` widens so a correction can't corrupt a
    // partially-covered segment). Blanking that erased whole sentences when the
    // reviewer had selected two words inside one. So the value is rebuilt from
    // the covered fragments the selection did NOT touch — making Remove exactly
    // "Fix Translation with those words deleted for you", which is the one
    // reading of it that stays true in every view: where nothing was widened,
    // every covered fragment is selected and the result is still ''.
    // PLAT-11648 — the selected run is replaced by nothing and the surrounding
    // fragments close up. Remove ALWAYS splices, in every span shape: unlike
    // `confirm` it never had an editing box, so there is no whole-correction
    // text standing in for the span. Splicing an empty value into a stored
    // correction deletes the highlighted fragment out of it; handing the empty
    // string straight through would store the whole correction blank
    // (pull request #3566 review).
    await authorSpanValue(current, spliceSelectedValue(current, ''));
  }, [editor, pendingSpan, authorSpanValue]);

  // PLAT-10257 (A2) — the directed span re-translate orchestration: send the
  // span's SOURCE to the backend with the reviewer's instruction, land the
  // returned translation as a reversible `origin: 'directed'` override. Unlike
  // `confirm` (which closes the editor optimistically before its store op),
  // this path keeps ALL state intact until the override is actually authored —
  // every refusal/failure returns a reason the host renders in place (A6).
  const confirmDirected = useCallback(
    async (instruction: string): Promise<ConfirmDirectedResult> => {
      const refuse = (
        reason: ConfirmDirectedFailureReason,
      ): ConfirmDirectedResult => {
        return { ok: false, reason };
      };

      // Step 1 — resolve the pending span exactly as `confirm` does.
      const current = editor;
      if (!current) return refuse('no-pending-span');
      const { model } = current;

      // Step 2 — TOUCH CHECK (locked decision 2026-07-16): the directed path
      // refuses ANY span touching an existing override. Within/exact-match is
      // the manual re-edit case (`existingOverrideSegmentId` — allowed for
      // `confirm`, refused here); extending/spans-two set `blockedReason` at
      // resolve time; the extracted predicate re-checks against the CURRENT
      // segment records as defense in depth — A3/A4 disable the UI for such
      // spans, but the op must never rely on that.
      if (model.existingOverrideSegmentId) {
        return refuse('blocked-within-fix');
      }
      if (current.blockedReason) {
        return refuse(
          current.blockedReason === 'spans-multiple-speakers'
            ? 'blocked-cross-speaker'
            : 'blocked-overlap',
        );
      }
      const segmentsById = new Map<string, ParentPhrase>();
      for (const seg of translationStore.getTranslation(language)
        ?.parentPhrases ?? []) {
        segmentsById.set(seg.id, seg);
      }
      if (spanTouchesOverride(model.orderedPhrases, segmentsById)) {
        return refuse('blocked-overlap');
      }

      // Step 3 — the CONTIGUOUS source-element run spanning the selection, in
      // source order. Located by the STABLE UUID (`coveredElementIds`), not the
      // clip-local integer index (`coveredIndices` is the id-less legacy
      // fallback) — the integer join misses in clip scope and refuses a valid
      // span (PLAT-10257 clip bug). The inclusive slice keeps the interleaved
      // whitespace elements the backend fragmenter needs for word boundaries —
      // without them the span comes back run-together (PLAT-10257 A7). A covered
      // element with no source is still a hard failure, never a silent skip: the
      // backend would re-translate a DIFFERENT source than the reviewer chose.
      let elements = collectSpanSourceElements(
        transcriptionElements,
        model.coveredElementIds,
        model.coveredIndices,
      );
      // Card 7 (§D) — the covered span no longer resolves. If a "Replace Term"
      // edit DELETED the covered token, fall back to the baseline slot's added-
      // token run as the re-translate source and re-key the authored override to
      // those tokens' fresh ids (else it re-orphans from the shared render hook).
      // No baseline / no live replacement ⇒ still `missing-source-element`.
      let reanchorAuthoredElementIds: string[] | undefined;
      if (elements === null) {
        const fallback = sourceBaseline
          ? resolveVanishedSpanSource(
              sourceBaseline,
              transcriptionElements,
              model.coveredElementIds,
              model.coveredIndices,
            )
          : null;
        if (!fallback) {
          return refuse('missing-source-element');
        }
        elements = fallback.sourceElements;
        reanchorAuthoredElementIds = fallback.freshElementIds;
      }

      // A6 — analytics (PLAT-10257, DESIGN §6). Fired from the hook — the
      // same layer the manual path fires `SpanReplaced` from — so both entry
      // points report identically and exactly once. The submit event fires
      // only when the request actually goes to the machine (refusals above
      // fire nothing — the manual precedent doesn't track refusals), so every
      // submit pairs with exactly one Completed or Failed below. Payload
      // ships the TRIMMED instruction LENGTH only, never the text (the
      // whole-scope `trackTranslationDirectedRetranslate` privacy posture).
      const analyticsPayload = {
        story_id: rootStore.storyId ?? undefined,
        language,
        scope: 'span' as const,
        instruction_length: instruction.trim().length,
      };
      trackTranslationReview_DirectedRetranslate(analyticsPayload);

      // Step 4 — the backend re-translate. On failure: author NOTHING, keep
      // the pending span/editor intact, and hand the reason up.
      let translatedValue: string;
      try {
        translatedValue = await translationStore.directedRetranslateSpan(
          language,
          elements,
          instruction,
        );
      } catch {
        trackTranslationReview_DirectedRetranslateFailed({
          ...analyticsPayload,
          reason: 'backend-failed',
        });
        return { ok: false, reason: 'backend-failed' };
      }

      // Step 4b (REVIEW F2) — reject an empty/whitespace-only machine result.
      // A 200 with a blank `translatedValue` (an LLM refusal or a degenerate
      // span) is a valid string that clears the repository's non-string narrow,
      // but authoring it would land a BLANK reversible override the reviewer
      // can't tell is empty until they open it. Treat it as a backend failure:
      // author NOTHING, keep the pending span/editor + instruction intact, and
      // hand the reason up — the same failure floor as the backend-call path.
      // Reuses the existing `backend-failed` reason (not a new `empty-result`
      // member) so the Failed-analytics reason union in EventTracker stays out
      // of scope; REVIEW F2's own disposition names `backend-failed` too.
      if (translatedValue.trim().length === 0) {
        trackTranslationReview_DirectedRetranslateFailed({
          ...analyticsPayload,
          reason: 'backend-failed',
        });
        return { ok: false, reason: 'backend-failed' };
      }

      // Step 5 — author the override. Mirrors `confirm`'s fresh-override
      // authoring exactly (indices union, fragmenter handling, covered stable
      // ids), plus the directed extras: a FORCED `origin: 'directed'` — which
      // never delegates to a single-phrase edit, so a null return reliably
      // means failure — and the recorded instruction (A5 renders it).
      try {
        const createdId = await translationStore.createOverrideSegment(
          language,
          unionOriginalIndices(model.orderedPhrases),
          translatedValue,
          {
            fragment: !!fragmenter,
            fragmenter,
            origin: 'directed',
            coveredElementIds: model.coveredElementIds,
            instruction,
            // Card 7 (§D) — re-key to the replacement's fresh ids on the
            // vanished-source path; undefined (no-op) on the normal path.
            reanchorAuthoredElementIds,
          },
        );
        if (createdId === null) {
          trackTranslationReview_DirectedRetranslateFailed({
            ...analyticsPayload,
            reason: 'author-failed',
          });
          return refuse('author-failed');
        }
        // Success — NOW close the editor + clear the lock + drop the selection
        // (the cleanup `confirm` does up front, deferred here because every
        // failure path must leave the editor open).
        setEditor(null);
        setPendingSpan(null);
        clearSpanReplaceLock(originalColumnRef.current);
        if (typeof window !== 'undefined') {
          window.getSelection()?.removeAllRanges();
        }
        trackTranslationReview_DirectedRetranslateCompleted(analyticsPayload);
        // A3 post-save (locked decision 2026-07-16, coverage-kick ONLY): fire
        // the SAME seam the manual path fires — the workspace binds it to its
        // coverage check. Fire-and-forget, like `confirm`. Deliberately NO
        // `resplitOverrideSegment`: a directed override keeps the tiling
        // `createOverrideSegment` produced.
        onSpanConfirmed?.({
          lang: language,
          coveredElementIds: model.coveredElementIds,
        });
        return { ok: true, segmentId: createdId };
      } catch {
        trackTranslationReview_DirectedRetranslateFailed({
          ...analyticsPayload,
          reason: 'author-failed',
        });
        return { ok: false, reason: 'author-failed' };
      }
    },
    [
      editor,
      translationStore,
      language,
      fragmenter,
      transcriptionElements,
      originalColumnRef,
      onSpanConfirmed,
      rootStore,
      sourceBaseline,
    ],
  );

  // Revert the open editor's covered span to its machine original (PLAT-10265,
  // the "Revert to original" action). Closes + unlocks optimistically, then
  // routes by data shape — mirroring the right-click flow:
  //   - override span (existingOverrideSegmentId set) → drop the whole override
  //     segment; the store reactivates the retired machine fragments + saves.
  //   - span overlapping corrections (overlappingOverrideSegmentIds, PLAT-11655)
  //     → drop each of those corrections, serially (each revert rewrites and
  //     saves the whole record, so two in flight would clobber one another).
  //     `revertTranslation` does not touch an override's fragments, so without
  //     this branch Revert on such a span would silently leave the corrections
  //     in place.
  //   - phrase-record edit(s) (no override segment — e.g. a single-word
  //     highlight that delegated to `editTranslatedPhrase`) → revert each
  //     covered phrase that carries an applied edit via `revertTranslation`.
  const revertOverride = useCallback(async () => {
    const current = editor;
    if (!current) return;
    const { existingOverrideSegmentId, orderedPhrases } = current.model;
    const overlapping = current.overlappingOverrideSegmentIds ?? [];
    setEditor(null);
    setPendingSpan(null);
    clearSpanReplaceLock(originalColumnRef.current);
    if (typeof window !== 'undefined') {
      window.getSelection()?.removeAllRanges();
    }
    try {
      if (existingOverrideSegmentId) {
        await translationStore.revertOverrideSegment(
          language,
          existingOverrideSegmentId,
        );
      } else {
        for (const segmentId of overlapping) {
          await translationStore.revertOverrideSegment(language, segmentId);
        }
        const overlappingSet = new Set(overlapping);
        const phrases = translationStore.getTranslation(language)?.phrases;
        for (const phrase of orderedPhrases) {
          // A fragment of a correction just reverted is gone; skip it.
          if (phrase.parentId && overlappingSet.has(phrase.parentId)) continue;
          if (isEdited(phrase, phrases)) {
            translationStore.revertTranslation(
              language,
              phrase.originalElementIds ?? [],
            );
          }
        }
      }
      trackTranslationReverted({
        story_id: rootStore.storyId ?? undefined,
        language,
      });
    } catch (error) {
      logger.error('span-replace revert failed', { language, error });
    }
  }, [editor, translationStore, language, originalColumnRef, rootStore]);

  // Whether the open span has nothing applied to revert — drives the editor's
  // disabled "Revert to original" state. Edited iff ANY covered phrase is edited
  // (the shared predicate covers both phrase-record edits and override
  // fragments); a fresh span or an unconfirmed draft has none.
  const revertDisabled = !editor
    ? true
    : !editor.model.orderedPhrases.some(p =>
        isEdited(p, translationStore.getTranslation(language)?.phrases),
      );

  return {
    pendingSpan,
    editor,
    openEditor,
    openForIndices,
    reDub,
    canReDub,
    canReDubSelection,
    reDubSelection,
    peekSelectionSpan: spanForSelection,
    confirm,
    removeSpan,
    confirmDirected,
    revertDisabled,
    revertOverride,
    cancel,
  };
}
